import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { useOutletContext } from 'react-router-dom';

export const PLATFORM_LABELS = Object.freeze({ ANDROID: 'Android', IOS: 'iOS' });
const VERSION_PATTERN = /^\d{1,9}\.\d{1,9}(?:\.\d{1,9})?$/;
const TESTFLIGHT_URL_PATTERN = /^https:\/\/testflight\.apple\.com\/\S+$/;
const EMPTY_FORM = Object.freeze({ platform: 'ANDROID', version: '', notes: '', testflight_url: '', file: null });

export function validateLibraryForm(form) {
    if (!VERSION_PATTERN.test(form.version.trim())) return '버전은 12.4.3 같은 숫자 형식으로 입력해 주세요.';
    if (form.platform === 'ANDROID') {
        if (!form.file) return 'APK 파일을 선택해 주세요.';
        if (!/\.apk$/i.test(form.file.name)) return 'APK 파일만 등록할 수 있습니다.';
    } else if (!TESTFLIGHT_URL_PATTERN.test(form.testflight_url.trim())) {
        return 'TestFlight 링크(https://testflight.apple.com/...)를 입력해 주세요.';
    }
    return null;
}

export function formatFileSize(bytes) {
    if (!bytes) return '-';
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

export function formatRegisteredAt(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    const kst = new Date(date.getTime() + 9 * 60 * 60 * 1000);
    return kst.toISOString().slice(0, 16).replace('T', ' ');
}

const AppLibrary = () => {
    const { role } = useOutletContext() || {};
    const isAdmin = role === 'admin';
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [form, setForm] = useState(EMPTY_FORM);
    const [uploadPercent, setUploadPercent] = useState(null);
    const [downloadingId, setDownloadingId] = useState(null);
    const [fileInputKey, setFileInputKey] = useState(0);

    const loadItems = useCallback(async () => {
        try {
            const response = await axios.get('/api/admin/app-library');
            setItems(response.data.items || []);
            setError('');
        } catch (loadError) {
            setError(loadError.response?.data?.message || '라이브러리 목록을 불러오지 못했습니다.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadItems(); }, [loadItems]);

    const updateForm = (field, value) => setForm((current) => ({ ...current, [field]: value }));

    const handleSubmit = async (event) => {
        event.preventDefault();
        const validationError = validateLibraryForm(form);
        if (validationError) {
            setError(validationError);
            return;
        }
        const body = new FormData();
        body.append('platform', form.platform);
        body.append('version', form.version.trim());
        body.append('notes', form.notes.trim());
        if (form.platform === 'ANDROID') body.append('file', form.file);
        else body.append('testflight_url', form.testflight_url.trim());

        setError('');
        setNotice('');
        setUploadPercent(0);
        try {
            await axios.post('/api/admin/app-library', body, {
                onUploadProgress: (progress) => {
                    if (progress.total) setUploadPercent(Math.round((progress.loaded / progress.total) * 100));
                }
            });
            setForm(EMPTY_FORM);
            setFileInputKey((key) => key + 1);
            setNotice('등록했습니다.');
            await loadItems();
        } catch (submitError) {
            setError(submitError.response?.data?.message || '등록에 실패했습니다.');
        } finally {
            setUploadPercent(null);
        }
    };

    const openItem = async (item) => {
        if (item.platform === 'IOS') {
            window.open(item.testflight_url, '_blank', 'noopener');
            return;
        }
        setDownloadingId(item.id);
        setError('');
        try {
            const response = await axios.get(`/api/admin/app-library/${item.id}/download`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(response.data);
            const link = document.createElement('a');
            link.href = url;
            link.download = `airconrg-stg-android-${item.version}.apk`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (downloadError) {
            setError('설치 파일을 받지 못했습니다.');
        } finally {
            setDownloadingId(null);
        }
    };

    const deleteItem = async (event, item) => {
        event.stopPropagation();
        if (!window.confirm(`${PLATFORM_LABELS[item.platform]} ${item.version} 항목을 삭제할까요?`)) return;
        try {
            await axios.delete(`/api/admin/app-library/${item.id}`);
            setNotice('삭제했습니다.');
            await loadItems();
        } catch (deleteError) {
            setError(deleteError.response?.data?.message || '삭제에 실패했습니다.');
        }
    };

    const uploading = uploadPercent !== null;

    return (
        <PageShell>
            <Header>
                <Eyebrow>STG TEST BUILDS</Eyebrow>
                <h1>라이브러리</h1>
                <p>테스트용 앱 설치 파일입니다. 항목을 누르면 Android는 APK를 받고, iOS는 TestFlight 링크가 열립니다.</p>
            </Header>

            {error && <Message role="alert" $error>{error}</Message>}
            {notice && <Message role="status">{notice}</Message>}

            {isAdmin && (
                <Card as="form" onSubmit={handleSubmit} aria-label="설치 파일 등록">
                    <h2>설치 파일 등록</h2>
                    <FormGrid>
                        <Field>
                            <label htmlFor="library-platform">플랫폼</label>
                            <select
                                id="library-platform"
                                value={form.platform}
                                onChange={(event) => updateForm('platform', event.target.value)}
                                disabled={uploading}
                            >
                                <option value="ANDROID">Android (APK)</option>
                                <option value="IOS">iOS (TestFlight)</option>
                            </select>
                        </Field>
                        <Field>
                            <label htmlFor="library-version">버전</label>
                            <input
                                id="library-version"
                                value={form.version}
                                placeholder="12.4.3"
                                onChange={(event) => updateForm('version', event.target.value)}
                                disabled={uploading}
                            />
                        </Field>
                        {form.platform === 'ANDROID' ? (
                            <Field>
                                <label htmlFor="library-file">APK 파일</label>
                                <input
                                    key={fileInputKey}
                                    id="library-file"
                                    type="file"
                                    accept=".apk"
                                    onChange={(event) => updateForm('file', event.target.files?.[0] || null)}
                                    disabled={uploading}
                                />
                            </Field>
                        ) : (
                            <Field>
                                <label htmlFor="library-testflight">TestFlight 링크</label>
                                <input
                                    id="library-testflight"
                                    value={form.testflight_url}
                                    placeholder="https://testflight.apple.com/join/..."
                                    onChange={(event) => updateForm('testflight_url', event.target.value)}
                                    disabled={uploading}
                                />
                            </Field>
                        )}
                    </FormGrid>
                    <Field>
                        <label htmlFor="library-notes">수정사항</label>
                        <textarea
                            id="library-notes"
                            rows={3}
                            value={form.notes}
                            placeholder="이번 빌드에서 바뀐 내용을 적어 주세요."
                            onChange={(event) => updateForm('notes', event.target.value)}
                            disabled={uploading}
                        />
                    </Field>
                    <Actions>
                        <PrimaryButton type="submit" disabled={uploading}>
                            {uploading ? `업로드 중 ${uploadPercent}%` : '등록'}
                        </PrimaryButton>
                    </Actions>
                </Card>
            )}

            <Card>
                <h2>설치 파일 목록</h2>
                {loading ? (
                    <Empty>불러오는 중…</Empty>
                ) : items.length === 0 ? (
                    <Empty>등록된 설치 파일이 없습니다.</Empty>
                ) : (
                    <List>
                        {items.map((item) => (
                            <Row
                                key={item.id}
                                type="button"
                                onClick={() => openItem(item)}
                                disabled={downloadingId === item.id}
                            >
                                <PlatformBadge $platform={item.platform}>{PLATFORM_LABELS[item.platform]}</PlatformBadge>
                                <RowBody>
                                    <strong>{item.version}</strong>
                                    <Notes>{item.notes || '수정사항 없음'}</Notes>
                                </RowBody>
                                <Meta>
                                    <span>{formatRegisteredAt(item.registered_at)}</span>
                                    <span>{item.platform === 'ANDROID' ? formatFileSize(item.file_size) : 'TestFlight'}</span>
                                </Meta>
                                <RowAction>
                                    {downloadingId === item.id ? '받는 중…' : item.platform === 'ANDROID' ? '다운로드' : '링크 열기'}
                                </RowAction>
                                {isAdmin && (
                                    <DeleteButton
                                        role="button"
                                        tabIndex={0}
                                        aria-label={`${PLATFORM_LABELS[item.platform]} ${item.version} 삭제`}
                                        onClick={(event) => deleteItem(event, item)}
                                        onKeyDown={(event) => { if (event.key === 'Enter') deleteItem(event, item); }}
                                    >
                                        삭제
                                    </DeleteButton>
                                )}
                            </Row>
                        ))}
                    </List>
                )}
            </Card>
        </PageShell>
    );
};

export default AppLibrary;

const PageShell = styled.main`
    min-height: calc(100vh - 130px);
    padding: 46px clamp(16px, 5vw, 80px) 80px;
    background: #f5f7fa;
    color: #172033;
`;

const Header = styled.header`
    margin-bottom: 24px;
    h1 { margin: 4px 0 6px; font-size: 32px; letter-spacing: -0.04em; }
    p { margin: 0; color: #667085; }
`;

const Eyebrow = styled.span`
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.18em;
    color: #16835f;
`;

const Message = styled.div`
    margin-bottom: 14px;
    padding: 11px 14px;
    border-radius: 10px;
    background: ${({ $error }) => $error ? '#fff0f0' : '#eaf8f2'};
    color: ${({ $error }) => $error ? '#b42318' : '#087451'};
    font-size: 13px;
`;

const Card = styled.section`
    margin-bottom: 20px;
    background: #fff;
    border: 1px solid #e1e6ee;
    border-radius: 18px;
    box-shadow: 0 12px 32px rgba(29, 41, 57, 0.06);
    padding: 26px;
    h2 { margin: 0 0 16px; font-size: 20px; }
`;

const FormGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 16px;
`;

const Field = styled.div`
    margin-bottom: 16px;
    label { display: block; margin-bottom: 8px; font-size: 13px; font-weight: 750; }
    input, select, textarea {
        width: 100%;
        box-sizing: border-box;
        border: 1px solid #d8dee8;
        border-radius: 10px;
        background: #fbfcfd;
        padding: 11px 13px;
        font: inherit;
        color: #172033;
        &:focus { outline: 2px solid rgba(22, 131, 95, 0.15); border-color: #16835f; }
    }
    textarea { resize: vertical; line-height: 1.55; }
`;

const Actions = styled.div`
    display: flex;
    justify-content: flex-end;
`;

const PrimaryButton = styled.button`
    min-width: 120px;
    padding: 11px 18px;
    border: 1px solid #16835f;
    border-radius: 10px;
    background: #16835f;
    color: #fff;
    font-weight: 750;
    cursor: pointer;
    &:disabled { opacity: 0.6; cursor: default; }
`;

const Empty = styled.p`
    margin: 0;
    padding: 30px 0;
    text-align: center;
    color: #667085;
`;

const List = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const Row = styled.button`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 14px;
    width: 100%;
    padding: 16px 18px;
    border: 1px solid #e1e6ee;
    border-radius: 14px;
    background: #fff;
    color: #172033;
    text-align: left;
    font: inherit;
    cursor: pointer;
    &:hover { border-color: #16835f; background: #f6fbf9; }
    &:disabled { opacity: 0.7; cursor: progress; }
`;

const PlatformBadge = styled.span`
    flex: 0 0 auto;
    min-width: 64px;
    padding: 6px 10px;
    border-radius: 999px;
    background: ${({ $platform }) => $platform === 'ANDROID' ? '#dff6ec' : '#e6efff'};
    color: ${({ $platform }) => $platform === 'ANDROID' ? '#087451' : '#1d4ed8'};
    font-size: 12px;
    font-weight: 800;
    text-align: center;
`;

const RowBody = styled.span`
    display: flex;
    flex: 1 1 240px;
    flex-direction: column;
    gap: 4px;
    strong { font-size: 16px; }
`;

const Notes = styled.span`
    color: #475467;
    font-size: 13px;
    white-space: pre-line;
`;

const Meta = styled.span`
    display: flex;
    flex-direction: column;
    gap: 2px;
    color: #667085;
    font-size: 12px;
    text-align: right;
`;

const RowAction = styled.span`
    color: #16835f;
    font-size: 13px;
    font-weight: 800;
`;

const DeleteButton = styled.span`
    padding: 6px 10px;
    border: 1px solid #f3c1bd;
    border-radius: 8px;
    color: #b42318;
    font-size: 12px;
    font-weight: 700;
    &:hover { background: #fff0f0; }
`;
